import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../js/hooks/useAuth";
import { parseFilmSearch } from "@tour/film.js";
import { demoVideoUrl, JOURNEY_LINK_PROPS } from "../../js/utils/siteLinks";
import ModalBase from "./ModalBase";

const KEY = "dh_admin_desk_start";
const SHOW = "show";
const DONE = "done";

function starterLinks(session) {
  return [
    { to: "/admin/messages", label: "Messages" },
    { to: "/admin/beta", label: "Beta Feedback" },
    { to: "/admin/hub/hall", label: "The Hub" },
    { href: demoVideoUrl(session), label: "Demo Video" },
  ];
}

function gateValue() {
  try {
    return sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
}

function writeGate(value) {
  try {
    sessionStorage.setItem(KEY, value);
  } catch {
    /* ignore */
  }
}

export function clearAdminStartGate() {
  writeGate(SHOW);
}

export default function AdminStarterModal({ onOpenChange }) {
  const { session } = useAuth();
  const { search } = useLocation();
  const filmOn = parseFilmSearch(search).on;
  const isAdmin = session?.role === "admin";
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!isAdmin || filmOn) {
      setOpen(false);
      onOpenChange?.(false);
      return;
    }
    if (gateValue() === DONE) {
      setOpen(false);
      onOpenChange?.(false);
      return;
    }
    writeGate(SHOW);
    setOpen(true);
    onOpenChange?.(true);
  }, [filmOn, isAdmin, onOpenChange]);

  useEffect(() => {
    if (!open) {
      document.documentElement.classList.remove("admin-start-open");
      return undefined;
    }
    document.documentElement.classList.add("admin-start-open");
    return () => document.documentElement.classList.remove("admin-start-open");
  }, [open]);

  function close() {
    writeGate(DONE);
    setOpen(false);
    onOpenChange?.(false);
  }

  if (!isAdmin || filmOn || !open || typeof document === "undefined") return null;

  return createPortal(
    <div className="admin-start-layer">
      <ModalBase isOpen title="Where would you like to start?" onClose={close} quiet welcome>
        <p className="muted">Choose a starting point. The Admin Desk stays behind this card.</p>
        <nav className="admin-start-list" aria-label="Admin starting points">
          {starterLinks(session).map((link) =>
            link.href ? (
              <a
                key={link.href}
                className="primary-btn admin-start-link"
                href={link.href}
                onClick={close}
                {...JOURNEY_LINK_PROPS}
              >
                {link.label}
              </a>
            ) : (
              <Link key={link.to} className="primary-btn admin-start-link" to={link.to} onClick={close}>
                {link.label}
              </Link>
            )
          )}
          <button type="button" className="ghost-btn admin-start-link" onClick={close}>
            Admin Desk
          </button>
        </nav>
      </ModalBase>
    </div>,
    document.body
  );
}
